import { AddDocumentDropdownButton } from "@/components/AddDocumentDropdownButton/AddDocumentDropdownButton";
import { AfpantMessages } from "@/components/AfpantMessages/AfpantMessages";
import { Button } from "@/components/Button/Button";
import { ButtonGroup } from "@/components/ButtonGroup/ButtonGroup";
import { ChangeTinglysingsmetodeDialog } from "@/components/ChangeTinglysingsmetodeDialog/ChangeTinglysingsmetodeDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog/ConfirmDialog";
import { CustomerLayout } from "@/components/CustomerLayout/CustomerLayout";
import { DocumentList } from "@/components/DocumentList/DocumentList";
import { DocumentTitle } from "@/components/DocumentTitle/DocumentTitle";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Forsendelse } from "@/components/Forsendelse/Forsendelse";
import { RegisterenhetListDialog } from "@/components/RegisterenhetListDialog/RegisterenhetListDialog";
import { Stack } from "@/components/Stack/Stack";
import { TinglysDocumentsDialog } from "@/components/TinglysDocumentsDialog/TinglysDocumentsDialog";
import { VitecDataDialog } from "@/components/VitecDataDialog/VitecDataDialog";
import { useApi } from "@/context/api";
import { useCurrentUser } from "@/context/currentUser";
import { routeLoaderApiClient } from "@/lib/api";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { parseSaksstatusAndBehandlingsutfall } from "@terralux/common/forsendelsesstatus";
import type { InferResponseType } from "hono";
import { useCallback, useState } from "react";
import css from "./index.module.scss";

export const Route = createFileRoute("/_authenticated-user/oppdrag/$tluxCaseId/")({
  loader: async ({ params }) => {
    const { tluxCaseId } = params;

    const res = await routeLoaderApiClient.oppdrag[":tluxCaseId"].$get({
      param: {
        tluxCaseId,
      },
    });
    if (res.ok === false) {
      throw new Error("Failed to fetch oppdrag");
    }

    return {
      oppdrag: await res.json(),
    };
  },
  head: ({ loaderData }) => {
    if (loaderData == null) {
      return {};
    }

    const { oppdrag } = loaderData;
    return {
      meta: [{ title: `Oppdrag ${oppdrag.oppdragsnummer} | Alice & Bob` }],
    };
  },
  component: Page,
});

function Page() {
  const { tluxCaseId } = Route.useParams();
  const { oppdrag } = Route.useLoaderData();

  const { currentUser } = useCurrentUser();
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  const handleToggleDocument = useCallback(
    ({ id, enabled }: { id: string; enabled: boolean }) => {
      const newSelectedDocumentIds = enabled
        ? [...selectedDocumentIds, id]
        : selectedDocumentIds.filter((selectedDocumentId) => selectedDocumentId !== id);

      setSelectedDocumentIds(newSelectedDocumentIds);
    },
    [selectedDocumentIds],
  );

  const selectedDocuments = oppdrag.documents.filter((document) => selectedDocumentIds.includes(document.documentId));

  const hasActiveForsendelser = oppdrag.forsendelser.some((forsendelse) => {
    // Only "sendTilTinglysing" forsendelser should be considered active
    if (forsendelse.operation !== "sendTilTinglysing") {
      return false;
    }

    // If we have not yet received forsendelsesstatus from Kartverket this is
    // definitely still active.
    if (forsendelse.forsendelsesstatus == null) {
      return true;
    }

    const parsedForsendelsesstatus = parseSaksstatusAndBehandlingsutfall(forsendelse.forsendelsesstatus);
    return parsedForsendelsesstatus.status === "pending";
  });

  return (
    <CustomerLayout>
      <DocumentTitle
        subtitle={
          <span className={css.subtitle}>
            {oppdrag.address}{" "}
            <RegisterenhetListDialog
              registerenheter={oppdrag.registerenheter}
              triggerButton={
                <Button variant="secondary" size="small">
                  Vis registerenheter
                </Button>
              }
            />
          </span>
        }
      >
        Oppdrag {oppdrag.oppdragsnummer}
      </DocumentTitle>

      <p className={css.tinglysingsmetodeToolbar}>
        Ønsket tinglysingsmetode: {oppdrag.tinglysingsmetode}{" "}
        <ChangeTinglysingsmetodeDialog
          tluxCaseId={tluxCaseId}
          tinglysingsmetode={oppdrag.tinglysingsmetode}
          triggerButton={<Button variant="secondary">Endre</Button>}
        />
      </p>

      <Stack size={60}>
        {oppdrag.documents.length > 0 ? (
          <DocumentList
            tluxCaseId={tluxCaseId}
            documents={oppdrag.documents.map((document) => ({
              ...document,
              selected: selectedDocumentIds.includes(document.documentId),
            }))}
            vedleggskategorier={oppdrag.vedleggskategorier}
            onToggleDocument={handleToggleDocument}
          />
        ) : (
          <p className={css.noDocuments}>Dette oppdraget har ingen dokumenter.</p>
        )}

        {oppdrag.afpantMessages.length > 0 && <AfpantMessages messages={oppdrag.afpantMessages} />}

        <ButtonGroup>
          <AddDocumentDropdownButton
            tluxCaseId={tluxCaseId}
            triggerButton={<Button variant="secondary">Legg til dokument</Button>}
          />
          <TinglysDocumentsDialog
            tluxCaseId={tluxCaseId}
            documents={oppdrag.documents.filter((document) => selectedDocumentIds.includes(document.documentId))}
            hasActiveForsendelser={hasActiveForsendelser}
            triggerButton={
              <Button variant="primary" disabled={selectedDocuments.length === 0}>
                Tinglys valgte
              </Button>
            }
          />
        </ButtonGroup>

        {oppdrag.forsendelser.map((forsendelse, index) => (
          <Forsendelse key={index} {...forsendelse} />
        ))}

        {(oppdrag.syncedFromVitecAt != null || currentUser.isSuperUser) && (
          <p className={css.synchronisation}>
            {oppdrag.syncedFromVitecAt != null && (
              <span className={css.syncedAt}>
                Sist synkronisert fra Vitec{" "}
                <FormattedDate value={oppdrag.syncedFromVitecAt} dateStyle="long" timeStyle="medium" />
              </span>
            )}
          </p>
        )}

        {currentUser.isSuperUser && (
          <ButtonGroup>
            <VitecDataDialog
              tluxCaseId={tluxCaseId}
              triggerButton={
                <Button variant="secondary" isSuperUserButton={true}>
                  Vis data fra Vitec
                </Button>
              }
            />

            <ScheduleVitecSynchronisationDialog
              tluxCaseId={tluxCaseId}
              triggerButton={
                <Button variant="secondary" isSuperUserButton={true}>
                  Start ny synkronisering
                </Button>
              }
            />
          </ButtonGroup>
        )}
      </Stack>
    </CustomerLayout>
  );
}

function ScheduleVitecSynchronisationDialog({
  tluxCaseId,
  triggerButton,
}: {
  tluxCaseId: string;
  triggerButton: React.ReactNode;
}) {
  const { client } = useApi();
  const [showDialog, setShowDialog] = useState(false);

  const $postSynchronise = client.superuser.oppdrag[":tluxCaseId"].synchronise.$post;
  const postSynchroniseMutation = useMutation<InferResponseType<typeof $postSynchronise>, Error>({
    mutationFn: async () => {
      const res = await $postSynchronise({ param: { tluxCaseId } });
      return await res.json();
    },
  });

  return (
    <ConfirmDialog
      open={showDialog}
      title="Start ny synkronisering"
      description="Synkronisering starter i bakgrunnen og kan ta et par minutter."
      confirmLabel="Start"
      isConfirming={postSynchroniseMutation.isPending}
      onConfirm={() => {
        postSynchroniseMutation.mutate();
        setShowDialog(false);
      }}
      onOpenChange={setShowDialog}
      triggerButton={triggerButton}
    />
  );
}
